import { supabase } from '../lib/supabase';
import { 
  EnhancedRecommendationRequest, 
  LegacyRecommendationRequest,
  AiAssessment,
  ConversationResponse,
  PersonalContext,
  EmailInteraction,
  BragSheetIntegration,
  TimelineAnalysis,
  WorkflowAutomation,
  RecommenderType,
  RelationshipStrength,
  EmailType
} from '../types/collegeRecommendations';
import recommendationLetterService from './recommendationLetterService';

export class EnhancedRecommendationService {
  
  /**
   * Get all recommendations with enhanced data, optionally include quick entries
   */
  async getEnhancedRecommendations(userId: string, includeQuickEntries: boolean = false): Promise<EnhancedRecommendationRequest[]> {
    try {
      // Get base recommendations using existing service
      const baseRecommendations = await recommendationLetterService.getRecommendations(userId);
      
      // Filter out quick entries unless specifically requested
      // Note: existing records may have is_quick_entry as null/undefined, treat as non-quick-entry
      const filteredRecommendations = includeQuickEntries 
        ? baseRecommendations 
        : baseRecommendations.filter(rec => rec.is_quick_entry !== true);
      
      // Enhance each recommendation with additional data
      const enhancedRecommendations = await Promise.all(
        filteredRecommendations.map(async (rec) => this.enhanceRecommendation(rec, userId))
      );
      
      return enhancedRecommendations;
    } catch (error) {
      console.error('Error fetching enhanced recommendations:', error);
      throw error;
    }
  }

  /**
   * Get only quick entries (potential recommenders list)
   */
  async getQuickEntries(userId: string): Promise<EnhancedRecommendationRequest[]> {
    try {
      // Get all recommendations including quick entries
      const allRecommendations = await this.getEnhancedRecommendations(userId, true);
      
      // Return only quick entries (explicitly true)
      return allRecommendations.filter(rec => rec.is_quick_entry === true);
    } catch (error) {
      console.error('Error fetching quick entries:', error);
      throw error;
    }
  }

  /**
   * Convert a quick entry to a full recommendation request
   */
  async promoteQuickEntryToRecommendation(
    quickEntryId: string,
    aiAssessment: AiAssessment,
    conversationResponses: ConversationResponse[],
    personalContext: PersonalContext,
    additionalInfo: {
      recommender_email: string;
      subject: string;
      relationship_length: string;
      due_date: Date;
      colleges: string[];
    }
  ): Promise<EnhancedRecommendationRequest> {
    try {
      // Update the quick entry with full recommendation data
      const updates = {
        ...additionalInfo,
        status: 'not_requested' as const,
        is_quick_entry: false,
        notes: JSON.stringify({
          ai_assessment: aiAssessment,
          conversation_responses: conversationResponses,
          personal_context: personalContext,
          original_notes: additionalInfo.notes || ''
        }),
        updated_at: new Date().toISOString()
      };

      await recommendationLetterService.updateRecommendation(quickEntryId, updates);
      
      // Get the updated recommendation
      const updatedRec = await recommendationLetterService.getRecommendation(quickEntryId);
      
      // Return enhanced version
      return this.enhanceRecommendation(updatedRec, updatedRec.user_id);
    } catch (error) {
      console.error('Error promoting quick entry to recommendation:', error);
      throw error;
    }
  }

  /**
   * Get a single recommendation by ID with enhanced data
   */
  async getRecommendationById(recommendationId: string): Promise<EnhancedRecommendationRequest | null> {
    try {
      // Get base recommendation using existing service
      const baseRecommendation = await recommendationLetterService.getRecommendation(recommendationId);

      if (!baseRecommendation) {
        return null;
      }

      // Return enhanced version
      return this.enhanceRecommendation(baseRecommendation, baseRecommendation.user_id);
    } catch (error) {
      console.error('Error fetching recommendation by ID:', error);
      throw error;
    }
  }

  /**
   * Create a new recommendation using the unified smart flow
   */
  async createSmartRecommendation(
    userId: string,
    aiAssessment: AiAssessment,
    conversationResponses: ConversationResponse[],
    personalContext: PersonalContext,
    basicInfo: {
      recommender_name: string;
      recommender_email: string;
      subject: string;
      relationship_length: string;
      due_date: Date;
      colleges: string[];
      notes?: string;
    }
  ): Promise<EnhancedRecommendationRequest> {
    try {
      // Create base recommendation
      const baseData = {
        ...basicInfo,
        user_id: userId,
        recommender_type: aiAssessment.recommenderType,
        status: 'not_requested' as const
      };

      const baseRecommendation = await recommendationLetterService.createRecommendation(baseData);
      
      // Create enhanced data structure
      const enhancedData: EnhancedRecommendationRequest = {
        ...baseRecommendation,
        ai_assessment: aiAssessment,
        conversation_responses: conversationResponses,
        personal_context: personalContext,
        current_phase: 'template',
        email_history: [],
        workflow_automation: this.initializeWorkflowAutomation(basicInfo.due_date),
        brag_sheet_integration: await this.getBragSheetIntegration(userId),
        timeline_analysis: this.calculateTimelineAnalysis(basicInfo.due_date)
      };

      return enhancedData;
    } catch (error) {
      console.error('Error creating smart recommendation:', error);
      throw error;
    }
  }

  /**
   * Update an existing smart recommendation
   */
  async updateSmartRecommendation(
    recommendationId: string,
    aiAssessment: AiAssessment,
    conversationResponses: ConversationResponse[],
    personalContext: PersonalContext,
    basicInfo: {
      recommender_name: string;
      recommender_email: string;
      subject: string;
      relationship_length: string;
      due_date: Date;
      colleges: string[];
      notes?: string;
    }
  ): Promise<EnhancedRecommendationRequest> {
    try {
      // Update base recommendation
      const updateData = {
        ...basicInfo,
        recommender_type: aiAssessment.recommenderType,
        status: 'not_requested' as const,
        // If this was a quick entry, mark it as a full recommendation now
        is_quick_entry: false
      };

      await recommendationLetterService.updateRecommendation(recommendationId, updateData);

      // Get updated recommendation
      const updatedRecommendation = await recommendationLetterService.getRecommendation(recommendationId);

      // Create enhanced data structure
      const enhancedData: EnhancedRecommendationRequest = {
        ...updatedRecommendation,
        ai_assessment: aiAssessment,
        conversation_responses: conversationResponses,
        personal_context: personalContext,
        current_phase: 'template',
        email_history: [],
        workflow_automation: this.initializeWorkflowAutomation(basicInfo.due_date),
        brag_sheet_integration: await this.getBragSheetIntegration(updatedRecommendation.user_id),
        timeline_analysis: this.calculateTimelineAnalysis(basicInfo.due_date)
      };

      return enhancedData;
    } catch (error) {
      console.error('Error updating smart recommendation:', error);
      throw error;
    }
  }

  /**
   * Save AI assessment and conversation data
   */
  async saveAiAssessmentData(
    recommendationId: string,
    aiAssessment: AiAssessment,
    conversationResponses: ConversationResponse[],
    personalContext: PersonalContext
  ): Promise<void> {
    try {
      // For now, store in notes field as JSON (could be separate table later)
      const enhancedData = {
        ai_assessment: aiAssessment,
        conversation_responses: conversationResponses,
        personal_context: personalContext
      };

      await recommendationLetterService.updateRecommendation(recommendationId, {
        notes: JSON.stringify(enhancedData)
      });
    } catch (error) {
      console.error('Error saving AI assessment data:', error);
      throw error;
    }
  }

  /**
   * Generate personalized email using AI assessment + framework templates
   */
  async generatePersonalizedEmail(
    recommendation: EnhancedRecommendationRequest,
    emailType: EmailType = 'request'
  ): Promise<{ subject: string; body: string; enhancements: string[] }> {
    try {
      console.log('🤖 Making AI API call to generate personalized email...');

      // Prepare data for AI API call
      const apiData = {
        action: emailType === 'follow_up' ? 'recommendation-followup' : 'recommendation-email',
        recommenderType: recommendation.ai_assessment?.recommenderType || recommendation.recommender_type,
        relationshipLevel: recommendation.ai_assessment?.relationshipStrength || 'medium',
        responses: recommendation.personal_context,
        studentName: recommendation.user_name || 'Student', // Assuming user_name exists or fallback
        recommenderName: recommendation.recommender_name,
        userId: recommendation.user_id,
        emailType,
        dueDate: recommendation.due_date
      };

      console.log('📤 Sending to AI API:', {
        action: apiData.action,
        recommenderType: apiData.recommenderType,
        relationshipLevel: apiData.relationshipLevel,
        responseKeys: Object.keys(apiData.responses || {}),
        emailType: apiData.emailType
      });

      // Make API call to AI endpoint
      const response = await fetch('/api/anthropic/suggestions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(apiData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ AI API error:', errorData);
        throw new Error(`AI API failed: ${errorData.error || response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ AI API success:', {
        hasEmail: !!result.email,
        emailLength: result.email?.length || 0,
        metadata: result.metadata
      });

      // The API returns { email: "content", metadata: {...} }
      // We need to format it for our expected structure
      let emailContent = result.email || 'AI generation failed - please try again';

      // Remove any "Subject:" line from the email body if present
      emailContent = emailContent.replace(/^Subject:.*\n\n?/i, '');

      // Replace any bracketed placeholders with actual names
      if (recommendation.recommender_name) {
        emailContent = emailContent.replace(/\[Teacher Name\]/gi, recommendation.recommender_name);
        emailContent = emailContent.replace(/\[Coach Name\]/gi, recommendation.recommender_name);
        emailContent = emailContent.replace(/\[Counselor Name\]/gi, recommendation.recommender_name);
        emailContent = emailContent.replace(/\[Mentor Name\]/gi, recommendation.recommender_name);
      }
      if (recommendation.user_name) {
        emailContent = emailContent.replace(/\[Your Name\]/gi, recommendation.user_name);
        emailContent = emailContent.replace(/\[Student\]/gi, recommendation.user_name);
      }

      const subject = `Recommendation Letter Request - ${recommendation.user_name || 'Student'}`;

      return {
        subject: subject,
        body: emailContent,
        enhancements: result.metadata?.source === 'fallback' ?
          ['Template-based fallback'] :
          ['AI-generated email', 'Personalized content']
      };

    } catch (error) {
      console.error('❌ Error in AI email generation:', error);

      // Fallback to template-based approach if AI fails
      console.log('🔄 Falling back to template-based generation...');
      return this.generateFallbackEmail(recommendation, emailType);
    }
  }

  /**
   * Fallback email generation using templates (in case AI fails)
   */
  private generateFallbackEmail(
    recommendation: EnhancedRecommendationRequest,
    emailType: EmailType
  ): { subject: string; body: string; enhancements: string[] } {
    try {
      const { emailTemplates } = require('../components/College/recommendationFramework');

      // Get appropriate template
      const templateKey = emailType === 'follow_up'
        ? `${recommendation.recommender_type}_follow_up`
        : emailType === 'thank_you'
        ? `${recommendation.recommender_type}_thank_you`
        : recommendation.recommender_type;
      const template = emailTemplates[templateKey] || emailTemplates.teacher;

      // Apply basic personalization
      return this.applyAiPersonalization(
        template,
        recommendation.personal_context,
        recommendation.ai_assessment,
        recommendation
      );
    } catch (fallbackError) {
      console.error('❌ Even fallback generation failed:', fallbackError);

      // Ultimate fallback
      return {
        subject: `Recommendation Letter Request - ${recommendation.user_name || 'Student'}`,
        body: `Dear ${recommendation.recommender_name || '[Teacher Name]'},\n\nI hope this email finds you well. I am writing to request a letter of recommendation for my college applications.\n\n${recommendation.personal_context?.memorable_trait || 'I believe you know me well as a student'}, and I would be honored if you would consider supporting my college goals.\n\nPlease let me know if you would be willing to write a recommendation letter for me.\n\nThank you for your time and consideration.\n\nBest regards,\n${recommendation.user_name || 'Student'}`,
        enhancements: ['Basic template fallback']
      };
    }
  }

  /**
   * Update workflow phase
   */
  async updateWorkflowPhase(
    recommendationId: string,
    phase: EnhancedRecommendationRequest['current_phase']
  ): Promise<void> {
    try {
      // Auto-progress status based on phase
      let newStatus = undefined;
      if (phase === 'tracking') {
        newStatus = 'in_progress';
      }
      
      const updates: any = {};
      if (newStatus) {
        updates.status = newStatus;
      }
      
      await recommendationLetterService.updateRecommendation(recommendationId, updates);
    } catch (error) {
      console.error('Error updating workflow phase:', error);
      throw error;
    }
  }

  /**
   * Save enhanced email with AI enhancements tracking
   */
  async saveEnhancedEmail(
    recommendationId: string,
    emailType: EmailType,
    content: string,
    subject: string,
    aiEnhancements: string[] = [],
    status: 'draft' | 'sent' = 'draft'
  ): Promise<void> {
    try {
      // Use existing email saving functionality
      await recommendationLetterService.saveEmailContent(recommendationId, {
        type: emailType,
        content,
        status
      });
      
      // Could add AI enhancement tracking here if needed
      // For now, using existing email_history structure
    } catch (error) {
      console.error('Error saving enhanced email:', error);
      throw error;
    }
  }

  /**
   * Private helper methods
   */
  private async enhanceRecommendation(
    baseRec: LegacyRecommendationRequest,
    userId: string
  ): Promise<EnhancedRecommendationRequest> {
    // Parse enhanced data from notes if it exists
    let enhancedData: any = {};
    if (baseRec.notes) {
      try {
        const parsed = JSON.parse(baseRec.notes);
        if (parsed.ai_assessment) {
          enhancedData = parsed;
        }
      } catch (e) {
        // Notes is just regular text, not enhanced data
      }
    }

    return {
      ...baseRec,
      ai_assessment: enhancedData.ai_assessment,
      conversation_responses: enhancedData.conversation_responses || [],
      personal_context: enhancedData.personal_context || {},
      current_phase: this.determineCurrentPhase(baseRec),
      email_history: await this.getEmailHistory(baseRec.id),
      workflow_automation: this.initializeWorkflowAutomation(baseRec.due_date),
      brag_sheet_integration: await this.getBragSheetIntegration(userId),
      timeline_analysis: this.calculateTimelineAnalysis(baseRec.due_date)
    };
  }

  private determineCurrentPhase(rec: LegacyRecommendationRequest): EnhancedRecommendationRequest['current_phase'] {
    if (rec.status === 'submitted') return 'tracking';
    if (rec.status === 'in_progress') return 'email';
    return 'assessment';
  }

  private async getEmailHistory(recommendationId: string): Promise<EmailInteraction[]> {
    try {
      const data = await recommendationLetterService.getEmailHistory(recommendationId);
      const history: EmailInteraction[] = [];
      
      // Convert existing email data to enhanced format
      if (data.request_email_content) {
        history.push({
          id: `${recommendationId}_request`,
          type: 'request',
          subject: 'Recommendation Letter Request',
          content: data.request_email_content,
          status: data.request_email_sent_date ? 'sent' : 'draft',
          timestamp: data.request_email_sent_date || new Date()
        });
      }
      
      if (data.follow_up_email_content) {
        history.push({
          id: `${recommendationId}_follow_up`,
          type: 'follow_up',
          subject: 'Materials for Recommendation Letter',
          content: data.follow_up_email_content,
          status: data.follow_up_email_sent_date ? 'sent' : 'draft',
          timestamp: data.follow_up_email_sent_date || new Date()
        });
      }
      
      return history;
    } catch (error) {
      console.error('Error getting email history:', error);
      return [];
    }
  }

  private async getBragSheetIntegration(userId: string): Promise<BragSheetIntegration> {
    try {
      // Check brag sheet status (using same logic as CollegeRecommendations)
      const { data } = await supabase
        .from('student_academic_profiles')
        .select('ai_strategic_summary, ai_executive_summary')
        .eq('user_id', userId)
        .single();
      
      const exists = data && (data.ai_strategic_summary || data.ai_executive_summary);
      
      return {
        exists: !!exists,
        completionLevel: exists ? 'complete' : 'none',
        lastUpdated: exists ? new Date() : undefined,
        recommendationReadyFields: exists ? ['achievements', 'activities', 'academic_highlights'] : [],
        missingCriticalInfo: exists ? [] : ['basic_info', 'achievements', 'activities'],
        suggestedAction: exists ? 'export' : 'create',
        exportFormats: exists ? ['pdf', 'docx'] : []
      };
    } catch (error) {
      console.error('Error getting brag sheet integration:', error);
      return {
        exists: false,
        completionLevel: 'none',
        recommendationReadyFields: [],
        missingCriticalInfo: ['basic_info', 'achievements', 'activities'],
        suggestedAction: 'create',
        exportFormats: []
      };
    }
  }

  private calculateTimelineAnalysis(dueDate: Date | string | null): TimelineAnalysis | undefined {
    if (!dueDate) return undefined;
    
    const now = new Date();
    const dueDateObj = dueDate instanceof Date ? dueDate : new Date(dueDate);
    
    // Check if the date is valid before performing calculations
    if (isNaN(dueDateObj.getTime())) {
      return {
        status: 'error' as const,
        message: 'Invalid due date provided',
        weeksUntilDue: 0,
        recommendations: ['Please verify the due date and try again']
      };
    }
    
    const weeksUntilDue = Math.ceil((dueDateObj.getTime() - now.getTime()) / (1000 * 60 * 60 * 24 * 7));
    
    let status: TimelineAnalysis['status'];
    let message: string;
    let recommendations: string[];
    
    if (weeksUntilDue < 0) {
      status = 'overdue';
      message = 'This deadline has passed. Contact colleges about extensions.';
      recommendations = ['Contact admissions offices immediately', 'Explain your situation honestly'];
    } else if (weeksUntilDue < 3) {
      status = 'critical';
      message = 'Very short notice. Apologize for timing and provide all materials immediately.';
      recommendations = ['Apologize for short notice', 'Provide all materials upfront', 'Follow up quickly'];
    } else if (weeksUntilDue < 6) {
      status = 'tight';
      message = 'Getting tight. Request ASAP and be extra prepared with materials.';
      recommendations = ['Send request today', 'Have all materials ready', 'Be very organized'];
    } else if (weeksUntilDue < 8) {
      status = 'good';
      message = 'Good timing - adequate notice for a quality letter.';
      recommendations = ['Follow standard timeline', 'Prepare materials thoughtfully'];
    } else {
      status = 'excellent';
      message = 'Excellent timing - plenty of notice for a strong letter.';
      recommendations = ['Take time to build relationship', 'Prepare comprehensive materials'];
    }
    
    return {
      weeksUntilDue,
      status,
      message,
      recommendations,
      actionItems: this.generateActionItems(status, weeksUntilDue)
    };
  }

  private generateActionItems(status: TimelineAnalysis['status'], weeksUntilDue: number) {
    const items = [];
    
    if (status === 'critical' || status === 'overdue') {
      items.push({
        priority: 'high' as const,
        action: 'Send request immediately',
        description: 'Time is critical - send your request today'
      });
    } else {
      items.push({
        priority: 'medium' as const,
        action: 'Finalize request email',
        description: 'Review and personalize your request email'
      });
    }
    
    items.push({
      priority: 'medium' as const,
      action: 'Prepare brag sheet',
      description: 'Have your brag sheet ready to share'
    });
    
    if (weeksUntilDue > 4) {
      items.push({
        priority: 'low' as const,
        action: 'Schedule follow-up',
        description: 'Plan your follow-up timeline'
      });
    }
    
    return items;
  }

  private initializeWorkflowAutomation(dueDate: Date | string | null): WorkflowAutomation {
    if (!dueDate) {
      return {
        autoProgressEnabled: true,
        nextSuggestedAction: {
          action: 'send_request',
          description: 'Send your personalized request email',
          urgency: 'medium'
        },
        reminders: []
      };
    }
    
    const dueDateObj = dueDate instanceof Date ? dueDate : new Date(dueDate);
    
    // Check if the date is valid before performing calculations
    if (isNaN(dueDateObj.getTime())) {
      return {
        autoProgressEnabled: true,
        nextSuggestedAction: {
          action: 'send_request',
          description: 'Send your personalized request email',
          urgency: 'medium'
        },
        reminders: []
      };
    }
    
    const weeksUntilDue = Math.ceil((dueDateObj.getTime() - Date.now()) / (1000 * 60 * 60 * 24 * 7));
    
    return {
      autoProgressEnabled: true,
      nextSuggestedAction: {
        action: 'send_request',
        description: 'Send your personalized request email',
        urgency: weeksUntilDue < 4 ? 'high' : weeksUntilDue < 6 ? 'medium' : 'low'
      },
      reminders: []
    };
  }

  private applyAiPersonalization(
    template: any,
    personalContext: PersonalContext,
    aiAssessment?: AiAssessment,
    recommendation?: EnhancedRecommendationRequest
  ): { subject: string; body: string; enhancements: string[] } {
    console.log('applyAiPersonalization called with recommendation due_date:', recommendation?.due_date);
    let personalizedBody = template.body;
    let personalizedSubject = template.subject;
    const enhancements: string[] = [];

    // Apply personal context replacements
    Object.entries(personalContext).forEach(([key, value]) => {
      if (value) {
        const placeholder = `{{${key.toUpperCase()}}}`;
        if (personalizedBody.includes(placeholder)) {
          // Ensure value is a string to avoid date formatting issues
          const stringValue = typeof value === 'string' ? value : String(value);
          personalizedBody = personalizedBody.replace(new RegExp(placeholder, 'g'), stringValue);
          enhancements.push(`Personalized ${key.replace('_', ' ')}`);
        }
      }
    });

    // Apply basic replacements (same as before but enhanced)
    if (recommendation) {
      const isValidDate = (date: Date | null | undefined): date is Date => {
        return date instanceof Date && !isNaN(date.getTime());
      };

      const replacements = {
        '{{YOUR_NAME}}': '[Your Name]', // Will be filled by user context
        '{{TEACHER_NAME}}': recommendation.recommender_name,
        '{{COUNSELOR_NAME}}': recommendation.recommender_name,
        '{{COACH_NAME}}': recommendation.recommender_name,
        '{{MENTOR_NAME}}': recommendation.recommender_name,
        '{{SUBJECT}}': recommendation.subject,
        '{{MONTH}}': isValidDate(recommendation.due_date) ? new Intl.DateTimeFormat('en-US', { month: 'long' }).format(recommendation.due_date) : '[Month]',
        '{{DUE_DATE}}': isValidDate(recommendation.due_date) ? recommendation.due_date.toLocaleDateString() : '[Due Date]'
      };

      Object.entries(replacements).forEach(([placeholder, value]) => {
        personalizedBody = personalizedBody.replace(new RegExp(placeholder, 'g'), value);
        personalizedSubject = personalizedSubject.replace(new RegExp(placeholder, 'g'), value);
      });
    }

    // Add AI-specific enhancements based on relationship strength
    if (aiAssessment) {
      if (aiAssessment.relationshipStrength === 'high') {
        enhancements.push('Enhanced for strong relationship');
      } else if (aiAssessment.relationshipStrength === 'low') {
        enhancements.push('Adapted for newer relationship');
      }
    }

    return {
      subject: personalizedSubject,
      body: personalizedBody,
      enhancements
    };
  }
}

// Export singleton instance
export const enhancedRecommendationService = new EnhancedRecommendationService();
export default enhancedRecommendationService;